import React from 'react';
import { GlassCard, GlassBadge } from '@/design-system';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { formatPercent } from '@/utils/formatters';

interface CategoryCompliance {
  business: { actual: number; target: number; status: 'compliant' | 'non_compliant'; };
  tech: { actual: number; target: number; status: 'compliant' | 'non_compliant'; };
  support: { actual: number; target: number; status: 'compliant' | 'non_compliant'; };
}

interface CategoryComplianceChartProps {
  compliance: CategoryCompliance | null;
  isLoading: boolean;
}

const CategoryComplianceChart: React.FC<CategoryComplianceChartProps> = ({
  compliance,
  isLoading,
}) => {
  const categories = [
    {
      key: 'business' as const,
      label: 'Business',
      description: 'Customer-facing features',
      color: 'text-accent-blue',
      bgColor: 'bg-blue-500/10',
      barColor: 'bg-blue-500',
    },
    {
      key: 'tech' as const,
      label: 'Technology',
      description: 'Infrastructure & platform',
      color: 'text-sky-400',
      bgColor: 'bg-sky-500/10',
      barColor: 'bg-sky-500',
    },
    {
      key: 'support' as const,
      label: 'Support',
      description: 'Operations & maintenance',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      barColor: 'bg-orange-500',
    },
  ];

  if (isLoading) {
    return (
      <GlassCard className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-2 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!compliance) {
    return (
      <GlassCard className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Category Compliance</h3>
        <div className="text-center text-muted-foreground/60 py-8">
          No compliance data available
        </div>
      </GlassCard>
    );
  }

  const overallCompliance = Object.values(compliance).every(cat => cat.status === 'compliant');

  return (
    <GlassCard className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Investment by Category</h2>
          <p className="text-sm text-muted-foreground/60 mt-0.5">
            Target: 75% Business, 15% Tech, 10% Support
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-category-business"></div>
            <span className="text-muted-foreground">Business</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-info"></div>
            <span className="text-muted-foreground">Tech</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-success"></div>
            <span className="text-muted-foreground">Support</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {categories.map((category) => {
          const data = compliance[category.key];
          const categoryColors = {
            business: { bg: 'bg-category-business-bg', text: 'text-category-business-text', letter: 'B' },
            tech: { bg: 'bg-status-info-bg', text: 'text-status-info-text', letter: 'T' },
            support: { bg: 'bg-status-success-bg', text: 'text-status-success-text', letter: 'S' },
          } as const;
          const colors = categoryColors[category.key];

          return (
            <div key={category.key} className={`flex items-center justify-between p-3 rounded-xl ${colors.bg}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                  <span className={`${colors.text} font-bold`}>{colors.letter}</span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{category.label}</p>
                  <p className="text-sm text-muted-foreground/60">{Math.round(data.actual * 28.47)} TWs</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-xl font-bold ${colors.text}`}>{formatPercent(data.actual, { decimals: 0 })}</p>
                <p className="text-xs text-muted-foreground/60">Target: {data.target}%</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {overallCompliance ? (
              <CheckCircle className="w-5 h-5 text-status-success" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-status-warning" />
            )}
            <span className="text-sm font-medium text-muted-foreground">
              {Object.values(compliance).filter(cat => cat.status === 'compliant').length}/3 Categories Compliant
            </span>
          </div>
          <GlassBadge variant={overallCompliance ? 'success' : 'warning'}>
            {overallCompliance ? 'Within Target' : 'Needs Attention'}
          </GlassBadge>
        </div>
      </div>
    </GlassCard>
  );
};

export default CategoryComplianceChart;